import { useAppSelector } from 'hooks/redux';
import React, { useMemo, useEffect } from 'react';
import type { RouterProviderProps } from 'react-router';
import {
  RouterProvider,
  type ShouldRevalidateFunctionArgs,
  createBrowserRouter,
  redirect,
  useRouteError,
} from 'react-router-dom';

import { accountsActions, accountsSelectors } from 'app-components/flux/accounts';
import { authSelectors } from 'app-components/flux/auth';
import {
  billableInvoiceItemsActions,
  billableInvoiceItemsSelectors,
} from 'app-components/flux/billable-invoice-items/billableInvoiceItemsSlice';
import { categoriesActions, categoriesSelectors } from 'app-components/flux/categories';
import { customersActions, customersSelectors } from 'app-components/flux/customers';
import { datasetsUtils } from 'app-components/flux/datasets';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import type { InvoiceData } from 'app-components/flux/invoice-actions';
import { viewInvoiceData } from 'app-components/flux/invoice-actions';
import { invoiceItemsActions, invoiceItemsSelectors } from 'app-components/flux/invoice-items/invoiceItemsSlice';
import { preferencesActions } from 'app-components/flux/preferences';
import { projectsActions, projectsSelectors } from 'app-components/flux/projects';
import { salesTaxesActions, salesTaxesSelectors } from 'app-components/flux/sales-taxes/salesTaxesSlice';
import {
  scheduledTransactionsActions,
  scheduledTransactionsSelectors,
} from 'app-components/flux/scheduled-transactions';
import { tagsActions, tagsSelectors } from 'app-components/flux/tags';
import { taxFormsActions, taxFormsSelectors } from 'app-components/flux/tax-forms';
import { taxLineItemsActions, taxLineItemsSelectors } from 'app-components/flux/tax-line-items';
import * as taxesActions from 'app-components/flux/taxes/taxesActions';
import * as taxesSelectors from 'app-components/flux/taxes/taxesSelectors';
import { transactionsActions, transactionsSelectors } from 'app-components/flux/transactions';
import { fetchOnce } from 'app-components/hooks/utils';

import FallbackComponent from 'components/ErrorBoundary/FallbackComponent';
import Login from 'containers/App/Login';
import Logout from 'containers/App/Logout';

import { businessActions, businessSelectors } from 'app-components/flux/business';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { mileageTrackerPurposeActions } from 'app-components/flux/mileage-tracker-purpose/mileageTrackerPurposeSlice';
import { mileageTrackerRatesActions } from 'app-components/flux/mileage-tracker-rates/mileageTrackerRatesSlice';
import { mileageTrackerVehicleActions } from 'app-components/flux/mileage-tracker-vehicle/mileageTrackerVehicleSlice';
import { mileageTrackerActions } from 'app-components/flux/mileage-tracker/mileageTrackerSlice';
import { reportConfigurationsActions, reportConfigurationsSelectors } from 'app-components/flux/report-configurations';
import {
  transactionRulesActions,
  transactionRulesSelectors,
} from 'app-components/flux/transaction-rules/transactionRulesSlice';
import { mileageTrackerPath, taxPlannerPath } from 'containers/App/paths';
import ErrorPage from 'containers/ErrorPage';
import LayoutAuth from 'containers/LayoutAuth';
import LayoutDatasetEntitled from 'containers/LayoutDatasetEntitled';
import LayoutDatasets from 'containers/LayoutDatasets';
import LayoutLoggedOut from 'containers/LayoutLoggedOut';
import NotFoundPage from 'containers/NotFoundPage';
import UnauthenticatedHomePage from 'containers/UnauthenticatedHomePage/loadable';
import { useAppDispatch } from 'hooks/redux';
import { bugsnagNotify } from 'utils/core/bugsnag';

const ErrorBoundaryRouter = () => {
  const error = useRouteError() as Error;
  useEffect(() => {
    if (error) {
      bugsnagNotify(error);
    }
  }, [error]);

  return <FallbackComponent error={error} />;
};

const AppRouter = (props: RouterProviderProps) => {
  const dispatch = useAppDispatch();
  const isAuthTokenValid = useAppSelector((state) => Boolean(authSelectors.isAuthTokenValid(state)));
  const dataset = useAppSelector(authSelectors.getCurrentDataset);
  const isDatasetEntitled = useMemo(() => Boolean(dataset && datasetsUtils.hasActiveEntitlement(dataset)), [dataset]);

  const investmentsAcme = useAppSelector((state) =>
    Boolean(featureFlagsSelectors.getFeatureFlag(state, 'investmentsAcme')),
  );
  const reports = useAppSelector((state) => Boolean(featureFlagsSelectors.getFeatureFlag(state, 'reports')));
  const spendingWatchList = useAppSelector((state) =>
    Boolean(featureFlagsSelectors.getFeatureFlag(state, 'spendingWatchList')),
  );
  const goals = useAppSelector((state) => Boolean(featureFlagsSelectors.getFeatureFlag(state, 'goals')));
  const spendingPlan = useAppSelector((state) => Boolean(featureFlagsSelectors.getFeatureFlag(state, 'spendingPlan')));
  const upcoming = useAppSelector((state) => Boolean(featureFlagsSelectors.getFeatureFlag(state, 'upcoming')));
  const achievements = useAppSelector((state) => Boolean(featureFlagsSelectors.getFeatureFlag(state, 'achievements')));
  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const moveNonReportsFf = useAppSelector((reduxState) =>
    featureFlagsSelectors.getFeatureFlag(reduxState, 'reportsMoveRfndtrkrCrdtscr'),
  );
  const showReportsRefactor = useAppSelector((state) =>
    Boolean(featureFlagsSelectors.getFeatureFlag(state, 'reportsRefactor')),
  );
  const showNetWorth = useAppSelector((state) =>
    Boolean(featureFlagsSelectors.getFeatureFlag(state, 'netWorthV2Feature')),
  );

  const router = useMemo(() => {
    const routes = [
      {
        element: <LayoutLoggedOut />,
        ErrorBoundary: ErrorBoundaryRouter,
        children: [
          // globally available routes regardless of login or entitlement state ----------------------------------------
          {
            path: '/acknowledgements',
            lazy: () => import('containers/Legal/Acknowledgements'),
          },
          {
            path: '/web-acknowledgements.txt',
            lazy: () => import('containers/WebAcknowledgements'),
          },
          {
            path: '/error',
            element: <ErrorPage />,
          },
          {
            path: '/logout',
            element: <Logout />,
          },
          {
            path: '/login',
            element: <Login />,
          },
          {
            path: '/view-invoice',
            lazy: () => import('containers/ViewInvoice'),
            loader: async ({
              request,
            }: {
              request: Request;
            }): Promise<{ invoiceData: InvoiceData } | { error: true }> => {
              const JWT = new URL(request.url).searchParams.get('token');
              let respData: { invoiceData: InvoiceData; JWT: string | null } | { error: true } | undefined;

              try {
                if (!JWT) {
                  throw 'no invoice id param';
                }

                const invoiceData = await viewInvoiceData(JWT);

                if ('errors' in invoiceData) {
                  throw (invoiceData as any).errors?.[0] ?? 'Unknown error at view invoice';
                }

                respData = { invoiceData, JWT };
              } catch (err: any) {
                assert(false, err);
                respData = { error: true };
              }
              return respData;
            },
          },
          {
            path: '/automation',
            lazy: () => import('containers/Automation'),
          },
          {
            path: '/pick-your-plan',
            lazy: () => import('containers/PickYourPlanPage'),
          },
          {
            path: '/share-accept',
            lazy: () => import('containers/App/useShareAccept'),
          },
          {
            path: '/stripe-redirect',
            lazy: () =>
              import('components/Dialogs/AccountDiscovery/WidgetAuthenticator/StripeOAuth/StripeOAuthRedirect'),
          },
          {
            path: '/partner-auth',
            lazy: () => import('containers/PartnerAuth'),
          },
          {
            element: <LayoutAuth />,
            children: [
              ...(isAuthTokenValid
                ? [
                    // routes available after user logged-in -----------------------------------------------------------
                    {
                      element: <LayoutDatasets />,
                      children: [
                        ...(isDatasetEntitled
                          ? [
                              // routes for entitled dataset (main app) ------------------------------------------------
                              {
                                element: <LayoutDatasetEntitled />,
                                children: [
                                  {
                                    path: '/',
                                    lazy: () => import('containers/OverviewPage'),
                                    loader: () => {
                                      dispatch(
                                        preferencesActions.getPreferences({ section: 'shared', group: 'dataset' }),
                                      );
                                      return null;
                                    },
                                    shouldRevalidate: ({ currentUrl, nextUrl }: ShouldRevalidateFunctionArgs) => {
                                      return currentUrl.pathname === nextUrl.pathname;
                                    },
                                  },
                                  {
                                    path: '/transactions',
                                    lazy: () => import('containers/Transactions'),
                                    loader: () => {
                                      fetchOnce(accountsActions.getAccounts, accountsSelectors);
                                      fetchOnce(transactionsActions.getTransactions, transactionsSelectors);
                                      fetchOnce(categoriesActions.getCategories, categoriesSelectors);
                                      fetchOnce(tagsActions.getTags, tagsSelectors);
                                      return null;
                                    },
                                    shouldRevalidate: ({ currentUrl, nextUrl }: ShouldRevalidateFunctionArgs) => {
                                      // Always revalidate if path changed
                                      if (currentUrl.pathname !== nextUrl.pathname) {
                                        return true;
                                      }

                                      // Otherwise, only revalidate if displayNode query param changed
                                      const currentDisplayNode = currentUrl.searchParams.get('displayNode');
                                      const nextDisplayNode = nextUrl.searchParams.get('displayNode');

                                      return currentDisplayNode !== nextDisplayNode;
                                    },
                                  },
                                  {
                                    path: '/spending-plan/:tabs?/:selectedPlannedId?',
                                    lazy: () => import('containers/SpendingPlan'),
                                    loader: () => {
                                      if (!spendingPlan) {
                                        return redirect('/oops');
                                      }
                                      return null;
                                    },
                                  },
                                  {
                                    path: '/upcoming/:tabName?',
                                    lazy: () => import('containers/Upcoming'),
                                    loader: () => {
                                      if (!upcoming) {
                                        return redirect('/oops');
                                      }
                                      return null;
                                    },
                                  },
                                  {
                                    path: '/planning-tools/:tabName?',
                                    lazy: () => import('containers/PlanningTools'),
                                    loader: () => {
                                      if (!moveNonReportsFf) {
                                        return redirect('/oops');
                                      }
                                      return null;
                                    },
                                  },
                                  {
                                    path: '/watchlist/:selectedWatchlistId?',
                                    lazy: () => import('containers/SpendingWatchlist'),
                                    loader: () => {
                                      if (!spendingWatchList) {
                                        return redirect('/oops');
                                      }
                                      return null;
                                    },
                                  },
                                  {
                                    path: '/saved-reports/:tabName?',
                                    lazy: () => {
                                      fetchOnce(taxFormsActions.getTaxForms, taxFormsSelectors);
                                      fetchOnce(taxLineItemsActions.getTaxLineItems, taxLineItemsSelectors);
                                      fetchOnce(businessActions.getBusiness, businessSelectors);
                                      fetchOnce(
                                        reportConfigurationsActions.getReportConfigurations,
                                        reportConfigurationsSelectors,
                                      );
                                      return import('containers/OpenSaveReports/OpenSaveReports');
                                    },
                                    loader: () => {
                                      if (!showReportsRefactor) {
                                        return redirect('/oops');
                                      }
                                      return null;
                                    },
                                  },
                                  {
                                    path: '/net-worth',
                                    lazy: () => {
                                      fetchOnce(
                                        reportConfigurationsActions.getReportConfigurations,
                                        reportConfigurationsSelectors,
                                      );
                                      return import('containers/NetWorth');
                                    },
                                    loader: () => {
                                      if (!showNetWorth) {
                                        return redirect('/oops');
                                      }
                                      return null;
                                    },
                                  },
                                  {
                                    path: '/reports/:tabName?/:groupBy?/:layoutView?',
                                    lazy: () => {
                                      fetchOnce(taxFormsActions.getTaxForms, taxFormsSelectors);
                                      fetchOnce(taxLineItemsActions.getTaxLineItems, taxLineItemsSelectors);
                                      fetchOnce(businessActions.getBusiness, businessSelectors);
                                      fetchOnce(
                                        reportConfigurationsActions.getReportConfigurations,
                                        reportConfigurationsSelectors,
                                      );
                                      return import('containers/OpenSaveReports');
                                    },
                                    loader: () => {
                                      if (!reports) {
                                        return redirect('/oops');
                                      }
                                      return null;
                                    },
                                  },
                                  {
                                    path: '/investing/:selectedInvestmentId?/:newsfeedArticleId?',
                                    lazy: () => import('containers/Investments/investmentsIndex'),
                                    loader: () => {
                                      if (!investmentsAcme) {
                                        return redirect('/oops');
                                      }
                                      return null;
                                    },
                                  },
                                  {
                                    path: '/goals/:goalId?',
                                    lazy: () => import('containers/Goals'),
                                    loader: () => {
                                      if (!goals) {
                                        return redirect('/oops');
                                      }
                                      return null;
                                    },
                                  },
                                  {
                                    path: '/settings/:settingSelected?/:itemSelected?',
                                    lazy: () => {
                                      fetchOnce(taxFormsActions.getTaxForms, taxFormsSelectors);
                                      fetchOnce(taxLineItemsActions.getTaxLineItems, taxLineItemsSelectors);
                                      fetchOnce(transactionRulesActions.fetch, transactionRulesSelectors);
                                      fetchOnce(categoriesActions.getCategories, categoriesSelectors);
                                      return import('containers/Settings');
                                    },
                                    loader: () => {
                                      return null;
                                    },
                                  },
                                  {
                                    path: '/get-started',
                                    lazy: () => import('containers/SetupPageAcme/flows/naturalFlowFunc'),
                                  },
                                  {
                                    path: '/achievements',
                                    lazy: () => import('containers/Achievements'),
                                    loader: () => {
                                      if (!achievements) {
                                        return redirect('/oops');
                                      }
                                      return null;
                                    },
                                  },
                                  {
                                    lazy: () => import('containers/Customers'),
                                    path: '/clients',
                                    loader: () => {
                                      if (!businessEnabled) {
                                        return redirect('/oops');
                                      }
                                      fetchOnce(customersActions.getCustomers, customersSelectors);
                                      fetchOnce(projectsActions.getProject, projectsSelectors);
                                      dispatch(billableInvoiceItemsActions.fetch());
                                      dispatch(invoiceItemsActions.fetch());
                                      return null;
                                    },
                                    shouldRevalidate: () => false,
                                    children: [
                                      {
                                        index: true,
                                        lazy: () =>
                                          import('containers/Customers/components/CustomerDashboardContainer'),
                                      },
                                      {
                                        path: ':customerId',
                                        lazy: () => import('containers/Customers/components/CustomerDetailsPage'),
                                      },
                                      {
                                        path: ':customerId/:projectId',
                                        lazy: () => import('containers/Projects/components/ProjectDetailsView'),
                                      },
                                    ],
                                  },
                                  {
                                    path: '/sales-and-billing',
                                    lazy: () => import('containers/SalesAndBilling'),

                                    loader: () => {
                                      if (!businessEnabled) {
                                        return redirect('/oops');
                                      }
                                      fetchOnce(customersActions.getCustomers, customersSelectors);
                                      fetchOnce(projectsActions.getProject, projectsSelectors);
                                      fetchOnce(invoiceItemsActions.fetch, invoiceItemsSelectors);
                                      fetchOnce(billableInvoiceItemsActions.fetch, billableInvoiceItemsSelectors);
                                      fetchOnce(salesTaxesActions.fetch, salesTaxesSelectors);
                                      return null;
                                    },
                                    shouldRevalidate: ({ currentUrl, nextUrl }: ShouldRevalidateFunctionArgs) => {
                                      return currentUrl.pathname !== nextUrl.pathname;
                                    },
                                    children: [
                                      { index: true, loader: () => redirect('/sales-and-billing/invoices') },
                                      {
                                        path: ':tab',
                                        lazy: () => import('containers/SalesAndBilling'),
                                      },
                                    ],
                                  },
                                  {
                                    path: '/sales-and-billing/products-and-services',
                                    lazy: () => import('containers/ProductsAndServices'),
                                    loader: () => {
                                      if (!businessEnabled) {
                                        return redirect('/oops');
                                      }
                                      fetchOnce(invoiceItemsActions.fetch, invoiceItemsSelectors);
                                      fetchOnce(categoriesActions.getCategories, categoriesSelectors);
                                      return null;
                                    },
                                  },
                                  {
                                    path: taxPlannerPath,
                                    lazy: () => import('containers/TaxPlanner'),
                                    loader: () => {
                                      fetchOnce(
                                        scheduledTransactionsActions.getScheduledTransactions,
                                        scheduledTransactionsSelectors,
                                      );
                                      fetchOnce(
                                        taxesActions.getTaxInputs, // TODO: replace with getTaxCalculations once ready
                                        taxesSelectors,
                                      );
                                      return null;
                                    },
                                  },
                                  {
                                    path: mileageTrackerPath,
                                    lazy: () => import('containers/MileageTracker'),
                                    loader: () => {
                                      if (!businessEnabled) {
                                        return redirect('/oops');
                                      }
                                      fetchOnce(businessActions.getBusiness, businessSelectors);
                                      dispatch(mileageTrackerActions.fetch());
                                      dispatch(mileageTrackerPurposeActions.fetch());
                                      dispatch(mileageTrackerVehicleActions.fetch());
                                      dispatch(mileageTrackerRatesActions.getRates());
                                      return null;
                                    },
                                    shouldRevalidate: ({ currentUrl, nextUrl }: ShouldRevalidateFunctionArgs) => {
                                      // Always revalidate if path changed
                                      if (currentUrl.pathname !== nextUrl.pathname) {
                                        return true;
                                      }
                                      return false;
                                    },
                                  },
                                  {
                                    path: '/oops',
                                    element: <NotFoundPage />,
                                  },
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
                                  {
                                    path: '/docs/:itemId?',
                                    lazy: () => import('containers/SharedComponentsPage'),
                                  },
///////////////////////////////////////////
                                  {
                                    path: '*',
                                    element: <NotFoundPage />,
                                  },
                                ],
                              },
                            ]
                          : [
                              // routes for logged-in user with no entitlement -----------------------------------------
                              {
                                path: '/',
                                element: <UnauthenticatedHomePage />,
                              },
                              {
                                path: '/no-subscription',
                                lazy: () => import('containers/NoSubscriptionPage'),
                              },
                              {
                                path: '*',
                                element: <LayoutAuth />, // could be replaced with any meaningful component
                              },
                            ]),
                      ],
                    },
                  ]
                : [
                    // ask user to login -------------------------------------------------------------------------------
                    {
                      path: '*',
                      element: <UnauthenticatedHomePage />,
                    },
                  ]),
            ],
          },
        ],
      },
    ];

    return createBrowserRouter(routes);
  }, [
    isAuthTokenValid,
    isDatasetEntitled,
    dispatch,
    spendingPlan,
    upcoming,
    moveNonReportsFf,
    spendingWatchList,
    showReportsRefactor,
    showNetWorth,
    reports,
    investmentsAcme,
    goals,
    achievements,
    businessEnabled,
  ]);

  const routerProvider = useMemo(() => <RouterProvider {...props} router={router} />, [props, router]);

  return routerProvider;
};

export default React.memo(AppRouter);
